import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { SSE } from 'sse.js';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../authentication.service';
import { ProjectRefService } from '../project-ref.service';
import { SseService } from './sse.service';

export interface CreditsConfig {
	starterNok: number;
	minTopupNok: number;
	maxTopupNok: number;
	lowBalanceThresholdNok: number;
	estimatedInteractionsPerNok: number;
}

export interface TopupInitiateResponse {
	redirectUrl: string;
	reference: string;
}

export type TopupStatus = 'pending' | 'authorized' | 'captured' | 'failed';

export interface TopupStatusResponse {
	status: TopupStatus;
	balanceNok?: number;
}

export interface BalanceUpdateEvent {
	balanceNok: number;
}

@Injectable({ providedIn: 'root' })
export class AiCreditsTopupService {
	private readonly http = inject(HttpClient);
	private readonly authService = inject(AuthenticationService);
	private readonly projectRefService = inject(ProjectRefService);
	private readonly sseService = inject(SseService);

	private readonly configUrl = `${environment.apiBaseUrl}${environment.endpoint.credits.config}`;
	private readonly initiateUrl = `${environment.apiBaseUrl}${environment.endpoint.credits.topupInitiate}`;
	private readonly statusUrl = `${environment.apiBaseUrl}${environment.endpoint.credits.topupStatus}`;
	private readonly balanceStreamUrl = `${environment.apiBaseUrl}${environment.endpoint.credits.balanceStream}`;

	private readonly _config = signal<CreditsConfig | null>(null);
	private _fetchingConfig = false;

	readonly config = this._config.asReadonly();
	readonly configLoaded = computed(() => this._config() !== null);

	fetchConfig(): void {
		if (this._fetchingConfig || this._config()) return;
		this._fetchingConfig = true;
		// finalize() (not the next/error callbacks) resets the guard, so it still clears even if
		// the request is aborted/cancelled without emitting either.
		this.http.get<CreditsConfig>(this.configUrl).pipe(
			finalize(() => { this._fetchingConfig = false; }),
		).subscribe({
			next: (config) => this._config.set(config),
			error: (err) => console.error('[AiCreditsTopup] Failed to fetch config:', err),
		});
	}

	initiate(amountNok: number): Observable<TopupInitiateResponse> {
		return this.http.post<TopupInitiateResponse>(this.initiateUrl, { amountNok });
	}

	checkStatus(reference: string): Observable<TopupStatusResponse> {
		return this.http.get<TopupStatusResponse>(`${this.statusUrl}/${encodeURIComponent(reference)}`);
	}

	/**
	 * The backend endpoint requires Authorization/ProjectToken headers (@GraphContext()),
	 * which native EventSource can't set — reuses the sse.js + SseService pattern already
	 * used for AI chat streaming (client-image-graph.service.ts), not the cookie-based
	 * EventSource pattern used for video-asset-watch.
	 */
	watchBalance(): Observable<BalanceUpdateEvent> {
		const token = this.authService.getToken();
		if (!token) {
			// Building the header as `Bearer ${token}` with a falsy token would send the literal
			// string "Bearer null"/"Bearer undefined" and open the stream unauthenticated instead
			// of failing loudly. Error instead — CreditsBarComponent.watchBalance() subscription
			// retries on a delay, so this resolves itself once a token is available.
			return throwError(() => new Error('[AiCreditsTopup] Cannot open balance stream: no auth token'));
		}

		const source = new SSE(this.balanceStreamUrl, {
			headers: {
				Authorization: `Bearer ${token}`,
				ProjectToken: this.projectRefService.getCurrentProjectToken(),
			},
			method: 'GET',
			start: false,
		});

		// SseService.createSSEObservable also emits a synthetic chat-stream-shaped 'init'
		// tuple on the underlying 'open' event — irrelevant here, filter down to real
		// balanceNok payloads only.
		return this.sseService.createSSEObservable<BalanceUpdateEvent | unknown>(source).pipe(
			filter((event): event is BalanceUpdateEvent =>
				typeof (event as BalanceUpdateEvent)?.balanceNok === 'number'),
		);
	}
}
